
import Vue from "vue";
import { format, utcToZonedTime } from "date-fns-tz";
import {
  getLocationPostedJobs,
  adminAcceptOffer,
  adminDeclineOffer,
  adminRequestResubmit,
  updateJobPaymentMethod
} from "@/api";
import EntityIdLink from "@/components/shared/EntityIdLink";
import { currentTimeZone } from "@/helpers";
import {
  formatJobPaymentMethod,
  isPaymentMethodEditable,
  paymentMethodOpts
} from "@/helpers/jobPaymentMethod";
import { formatJobRoleLabel } from "@/helpers/jobRoleLabel";
import {
  formatJobStatus,
  formatOfferStatus,
  JOB_STATUS_COLORS,
  OFFER_STATUS_COLORS
} from "@/helpers/adminEnumLabels";

type PendingAction = "accept" | "decline" | "resubmit";

export default Vue.extend({
  name: "PostedJobs",
  components: { EntityIdLink },
  props: {
    locationId: { type: Number, required: true },
    collapsed: { type: Boolean, default: false }
  },
  data() {
    return {
      loading: false,
      includePast: false,
      jobs: [] as any[],
      expanded: [] as any[],
      actionDialog: false,
      pendingAction: "accept" as PendingAction,
      pendingOffer: null as any,
      pendingJob: null as any,
      declineReason: "" as string,
      actingOfferId: "" as string,
      editingPaymentMethodJobId: null as number | null,
      editingPaymentMethodValue: null as number | null,
      savingPaymentMethodJobId: null as number | null,
      headers: [
        { text: "Job ID", value: "id", width: "90px" },
        { text: "Start Date", value: "startDate", width: "110px" },
        { text: "Type", value: "type", width: "130px" },
        { text: "Status", value: "status", width: "150px" },
        {
          text: "Payment Method",
          value: "paymentMethod",
          width: "200px",
          sortable: false
        },
        { text: "Booked With", value: "bookedWith", sortable: false },
        {
          text: "Offers",
          value: "offersCount",
          width: "80px",
          align: "center"
        },
        {
          text: "",
          value: "data-table-expand",
          width: "110px",
          sortable: false
        }
      ]
    };
  },
  computed: {
    actionTitle(): string {
      if (this.pendingAction === "accept") return "Accept this offer?";
      if (this.pendingAction === "decline") return "Decline this offer?";
      return "Request resubmit?";
    },
    actionBody(): string {
      const talent = this.pendingOffer
        ? `${this.pendingOffer.firstName || ""} ${this.pendingOffer.lastName ||
            ""}`.trim()
        : "the talent";
      const date = this.pendingJob ? this.pendingJob.startDate : "";
      if (this.pendingAction === "accept") {
        return `Accept ${talent}'s offer for the shift on ${date}? The shift books immediately and the talent is notified — same flow as the Job board view.`;
      }
      if (this.pendingAction === "decline") {
        return `Decline ${talent}'s offer for the shift on ${date}? The talent is notified — same flow as the Job board view.`;
      }
      return `Ask ${talent} to resubmit their offer for the shift on ${date}? They'll receive a push notification and an email.`;
    }
  },
  watch: {
    locationId: {
      immediate: true,
      handler() {
        this.expanded = [];
        this.cancelPaymentMethodEdit();
        this.fetch();
      }
    }
  },
  methods: {
    jobRoleLabel: formatJobRoleLabel,
    statusLabel(status: number): string {
      return formatJobStatus(status);
    },
    statusColor(status: number): string {
      return JOB_STATUS_COLORS[status] ?? "#9E9E9E";
    },
    offerStatusLabel(status: number): string {
      return formatOfferStatus(status);
    },
    offerStatusColor(status: number): string {
      return OFFER_STATUS_COLORS[status] ?? "#9E9E9E";
    },
    formatTime(utc: string): string {
      return format(utcToZonedTime(utc, currentTimeZone()), "HH:mm");
    },
    formatDateTime(utc: string): string {
      return format(
        utcToZonedTime(utc, currentTimeZone()),
        "MMM d yyyy, HH:mm"
      );
    },
    snackbar(text: string, color: string) {
      this.$store.commit("callSnackbar", { visible: true, text, color });
    },
    isPaymentMethodEditable,
    formatJobPaymentMethod,
    paymentMethodOptionsFor(item: any) {
      return paymentMethodOpts(item && item.country, item && item.isCentralBooked);
    },
    startPaymentMethodEdit(item: any) {
      if (!item || !isPaymentMethodEditable(item.status)) return;
      if (this.savingPaymentMethodJobId) return;
      this.editingPaymentMethodJobId = item.id;
      this.editingPaymentMethodValue =
        item.paymentMethod === null || item.paymentMethod === undefined
          ? null
          : Number(item.paymentMethod);
    },
    cancelPaymentMethodEdit() {
      if (this.savingPaymentMethodJobId) return;
      this.editingPaymentMethodJobId = null;
      this.editingPaymentMethodValue = null;
    },
    async savePaymentMethod(item: any, nextValue: number) {
      if (!item || this.savingPaymentMethodJobId) return;
      const paymentMethod = Number(nextValue);
      if (!Number.isInteger(paymentMethod)) return;
      if (Number(item.paymentMethod) === paymentMethod) {
        this.cancelPaymentMethodEdit();
        return;
      }
      const previous = item.paymentMethod;
      this.savingPaymentMethodJobId = item.id;
      this.editingPaymentMethodValue = paymentMethod;
      try {
        const result = await updateJobPaymentMethod(item.id, paymentMethod);
        item.paymentMethod = result.paymentMethod;
        this.editingPaymentMethodJobId = null;
        this.editingPaymentMethodValue = null;
        this.snackbar(
          `Payment method updated to ${result.paymentMethodLabel}`,
          "#13CA82"
        );
      } catch (error) {
        item.paymentMethod = previous;
        this.editingPaymentMethodValue =
          previous === null || previous === undefined ? null : Number(previous);
        const message =
          error instanceof Error
            ? error.message
            : "Could not update payment method";
        this.snackbar(message, "#FB7672");
        try {
          await this.fetch();
        } catch (_e) {
          /* keep restored local value */
        }
      } finally {
        this.savingPaymentMethodJobId = null;
      }
    },
    async fetch() {
      if (!this.locationId) return;
      this.loading = true;
      try {
        this.jobs = await getLocationPostedJobs(
          this.locationId,
          this.includePast
        );
      } catch (error) {
        console.error("Failed to load posted jobs", error);
        this.jobs = [];
        this.snackbar("Could not load posted jobs", "#FB7672");
      } finally {
        this.loading = false;
      }
    },
    confirmAction(job: any, offer: any, action: PendingAction) {
      this.pendingJob = job;
      this.pendingOffer = offer;
      this.pendingAction = action;
      this.declineReason = "";
      this.actionDialog = true;
    },
    async doAction() {
      if (!this.pendingOffer) return;
      const action = this.pendingAction;
      const offer = this.pendingOffer;
      this.actionDialog = false;
      this.actingOfferId = `${action}-${offer.id}`;
      try {
        if (action === "accept") {
          await adminAcceptOffer(offer.id);
          this.snackbar("Offer accepted — shift booked.", "#13CA82");
        } else if (action === "decline") {
          await adminDeclineOffer(offer.id, this.declineReason);
          this.snackbar("Offer declined.", "#13CA82");
        } else {
          await adminRequestResubmit(offer.id);
          this.snackbar(
            `Resubmit request sent to ${offer.firstName || "the talent"}.`,
            "#13CA82"
          );
        }
        await this.fetch();
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Action failed";
        this.snackbar(`Action failed: ${message}`, "#FB7672");
      } finally {
        this.actingOfferId = "";
        this.pendingOffer = null;
        this.pendingJob = null;
      }
    }
  }
});
